import { useState } from "react";
import type { TraceEnvelope } from "../lib/api";

/**
 * Trace viewer. Shown when debug mode is on, so a production failure can be
 * read and copied from the browser without opening a terminal.
 */
export function DebugPanel({ trace, error }: {
  trace: TraceEnvelope | null;
  error?: unknown;
}) {
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  if (!trace && !error) return null;

  const failures = trace?.steps.filter((s) => s.level === "error") ?? [];
  const slow = trace?.steps.filter((s) => (s.ms ?? 0) > 1500) ?? [];

  async function copy() {
    await navigator.clipboard.writeText(JSON.stringify({ error, trace }, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <section className="panel debug">
      <div className="debug-head">
        <h2>
          Trace{" "}
          {trace && <span className="num muted">{trace.traceId} · {trace.totalMs}ms · {trace.steps.length} steps</span>}
        </h2>
        <div>
          <button className="quiet" onClick={copy}>{copied ? "Copied" : "Copy trace"}</button>{" "}
          <button className="quiet" onClick={() => setOpen(!open)}>{open ? "Hide" : "Show"}</button>
        </div>
      </div>

      {failures.length > 0 && (
        <div className="warning">
          <strong>{failures.length} step{failures.length > 1 ? "s" : ""} failed.</strong>{" "}
          {failures[0].scope}: {failures[0].message}
          {hintOf(failures[0].detail) && <div style={{ marginTop: ".4rem" }}>{hintOf(failures[0].detail)}</div>}
        </div>
      )}

      {trace && !trace.verbose && (
        <p className="caveat">
          Showing failures and timings only. Add <code>?debug=1</code> to the URL
          for every step, if <code>DEBUG_TRACES</code> is on for this deployment.
        </p>
      )}

      {slow.length > 0 && (
        <p className="caveat">
          Slow steps: {slow.map((s) => `${s.scope} ${s.ms}ms`).join(", ")}
        </p>
      )}

      {open && (
        <div className="trace-list">
          {trace?.steps.map((s, i) => (
            <div className={`trace-step ${s.level}`} key={i}>
              <span className="num trace-t">{s.t}ms{s.ms != null && ` +${s.ms}`}</span>
              <span className="trace-scope">{s.scope}</span>
              <span>{s.message}</span>
              {s.detail !== undefined && (
                <pre className="trace-detail">{JSON.stringify(s.detail, null, 2)}</pre>
              )}
            </div>
          ))}
          {error !== undefined && (
            <div className="trace-step error">
              <span className="trace-scope">response</span>
              <pre className="trace-detail">{JSON.stringify(error, null, 2)}</pre>
            </div>
          )}
        </div>
      )}

      {trace && Object.keys(trace.counters).length > 0 && (
        <p className="caveat">
          {Object.entries(trace.counters).map(([k, v]) => `${k}: ${v.toLocaleString()}`).join(" · ")}
        </p>
      )}
    </section>
  );
}

function hintOf(detail: unknown): string | null {
  if (detail && typeof detail === "object" && "hint" in detail) {
    return String((detail as { hint: unknown }).hint);
  }
  return null;
}
